import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { getAllReferrals, getReferralsByPatient, createReferral } from '../../services/careCoordinationService';
import { DatePicker } from '@mui/x-date-pickers';

const statusOptions = ['ACTIVE', 'PENDING', 'ADMITTED', 'CANCELLED', 'COMPLETED'];
const statusColors = {
  'ACTIVE': 'primary',
  'PENDING': 'warning',
  'ADMITTED': 'success',
  'CANCELLED': 'error',
  'COMPLETED': 'info'
};

// Helper to support both flat and nested (attachments) data
const getField = (ref, field) => ref[field] || ref.attachments?.[field];

export default function ReferralsTab({ patientId, patients, facilityId }) {
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [form, setForm] = useState({
    patientId: '',
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    date: '',
    to: '',
    reason: '',
    status: 'ACTIVE',
  });

  const loadReferrals = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      
      if (patientId) {
        data = await getReferralsByPatient(patientId);
      } else {
        data = await getAllReferrals(facilityId);
      }
      
      console.log('ReferralsTab: Referrals data:', data);
      setReferrals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading referrals:', error);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, facilityId]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  useEffect(() => {
    // Apply filters and pagination
    let filtered = [...referrals];
    
    // Apply status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(ref => getField(ref, 'status') === statusFilter);
    }
    
    // Sort by recent first (by referralDate or createdAt)
    filtered.sort((a, b) => {
      const dateA = getField(a, 'referralDate') || getField(a, 'createdAt') || getField(a, 'date');
      const dateB = getField(b, 'referralDate') || getField(b, 'createdAt') || getField(b, 'date');
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      // Handle Firestore timestamp objects
      const timeA = dateA._seconds ? dateA._seconds * 1000 : new Date(dateA).getTime();
      const timeB = dateB._seconds ? dateB._seconds * 1000 : new Date(dateB).getTime();
      
      return timeB - timeA; // Recent first
    });
    
    setFilteredReferrals(filtered);
    setPage(0); // Reset to first page when filters change
  }, [referrals, statusFilter]);

  const handleOpen = () => {
    setForm({
      patientId: '',
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      date: '',
      to: '',
      reason: '',
      status: 'ACTIVE',
    });
    setOpen(true);
  };
  
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create referral with embedded patient data
      await createReferral({
        patientId: form.patientId,
        facilityId: facilityId, // Use the facilityId prop
        from: 'Internal Referral', // Default source
        to: form.to,
        reason: form.reason,
        status: form.status,
        priority: 'ROUTINE', // Default priority
        referralDate: new Date(form.date),
        notes: `Referral for ${form.firstName} ${form.lastName}`,
        createdBy: 'user', // Replace with actual user if available
        // Embed patient data directly in the referral document
        patient: {
          id: form.patientId,
          name: `${form.firstName} ${form.lastName}`,
          firstName: form.firstName,
          lastName: form.lastName,
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          mrn: form.patientId // Use patientId as MRN for now
        }
      });
      
      // Refresh referrals
      await loadReferrals();
      setOpen(false);
    } catch (error) {
      console.error('Error creating referral:', error);
      alert('Failed to create referral. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper to get patient name by id
  const getPatientName = (id) => {
    if (!id || !patients || patients.length === 0) return 'Unknown Patient';
    
    // Try different ways to find the patient
    let p = patients.find(p => p.patientId === id);
    if (!p) p = patients.find(p => p.id === id);
    if (!p) p = patients.find(p => p._id === id);
    
    if (p) {
      // Handle different patient name structures
      if (p.firstName && p.lastName) {
        return `${p.firstName} ${p.lastName}`;
      } else if (p.name) {
        return p.name;
      } else if (p.firstName) {
        return p.firstName;
      } else if (p.lastName) {
        return p.lastName;
      }
    }
    
    // If we can't find the patient, show a more user-friendly message
    return `Patient ${id.substring(0, 8)}...`;
  };

  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    
    try {
      if (dateField._seconds) {
        // Firestore timestamp
        return new Date(dateField._seconds * 1000).toLocaleDateString();
      } else {
        // Regular date string
        return new Date(dateField).toLocaleDateString();
      }
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    return statusColors[status] || 'default';
  };

  // Get paginated referrals
  const paginatedReferrals = filteredReferrals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading referrals...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">
            All Referrals ({filteredReferrals.length})
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Add Referral
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Referral Date</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedReferrals.map((ref) => {
              console.log('ReferralsTab: Processing referral:', {
                id: ref.id,
                patientId: ref.patientId,
                patient: ref.patient,
                patientName: ref.patient?.name,
                fallbackName: getPatientName(ref.patientId)
              });
              
              return (
                <TableRow key={ref.id}>
                  <TableCell>
                    {ref.patient?.name || getPatientName(ref.patientId)}
                  </TableCell>
                  <TableCell>{formatDate(getField(ref, 'referralDate') || getField(ref, 'createdAt') || getField(ref, 'date'))}</TableCell>
                  <TableCell>{getField(ref, 'to')}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {getField(ref, 'reason')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getField(ref, 'status') || 'UNKNOWN'} 
                      color={getStatusColor(getField(ref, 'status'))}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getField(ref, 'priority') || 'ROUTINE'} 
                      color="default"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" disabled>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredReferrals.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Referral</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              label="Patient ID"
              name="patientId"
              fullWidth
              value={form.patientId}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              label="First Name"
              name="firstName"
              fullWidth
              value={form.firstName}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              label="Last Name"
              name="lastName"
              fullWidth
              value={form.lastName}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              label="Gender"
              name="gender"
              fullWidth
              value={form.gender}
              onChange={handleChange}
              required
            />
            <DatePicker
              label="Date of Birth"
              value={form.dateOfBirth ? new Date(form.dateOfBirth) : null}
              onChange={(date) => handleChange({ target: { name: 'dateOfBirth', value: date ? date.toISOString().split('T')[0] : '' } })}
              slotProps={{
                textField: { margin: 'dense', fullWidth: true, required: true }
              }}
            />
            <DatePicker
              label="Date"
              value={form.date ? new Date(form.date) : null}
              onChange={(date) => handleChange({ target: { name: 'date', value: date ? date.toISOString().split('T')[0] : '' } })}
              slotProps={{
                textField: { margin: 'dense', fullWidth: true, required: true }
              }}
            />
            <TextField
              margin="dense"
              label="To (Provider/Facility)"
              name="to"
              fullWidth
              value={form.to}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              label="Reason"
              name="reason"
              fullWidth
              value={form.reason}
              onChange={handleChange}
              required
            />
            <TextField
              margin="dense"
              label="Status"
              name="status"
              select
              fullWidth
              value={form.status}
              onChange={handleChange}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
} 